import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';

interface Dependency { name: string; minVersion?: string; downloadId?: string; note?: string }
interface Download {
  downloadId: string;
  name: string;
  description?: string;
  category: 'device-service' | 'device-service-32' | 'driver' | 'software';
  version: string;
  platform?: 'windows' | 'mac' | 'linux' | 'any';
  deviceGroups?: string[];
  dependencies?: Dependency[];
  fileKey: string;
  fileName: string;
  sizeBytes?: number;
  contentType?: string;
  active: boolean;
}

type Draft = Omit<Download, 'downloadId' | 'active'> & { downloadId?: string; active: boolean };

const BLANK: Draft = {
  name: '', description: '', category: 'device-service', version: '', platform: 'windows',
  deviceGroups: [], dependencies: [], fileKey: '', fileName: '', active: true,
};

const CATEGORY_LABEL: Record<string, string> = {
  'device-service': 'Device Service',
  'device-service-32': 'Device Service (32-bit)',
  driver: 'Driver',
  software: 'Software',
};
const fmtSize = (b?: number) => (b ? (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`) : '—');

export function Downloads() {
  const isAdmin = useIsSiteAdmin();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['admin-downloads'], queryFn: () => api.get<Download[]>('/admin/downloads'), enabled: isAdmin });
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  if (!isAdmin) {
    return <div className="panel empty"><h3>Restricted</h3><p>Download management is available to Lakota platform staff only.</p></div>;
  }

  const rows = list.data ?? [];
  const editing: Draft | undefined = creating ? { ...BLANK } : (() => {
    const d = rows.find((r) => r.downloadId === selected);
    return d ? { ...d } : undefined;
  })();

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Download management</h1>
          <div className="sub">Publish versions of the Device Service and scanner drivers for customers to download.</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setCreating(true); setSelected(null); }}>+ New download</button>
      </div>

      <div className="admin-split">
        <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
          {list.isLoading ? <p className="loading-screen">// loading…</p> : rows.length === 0 ? (
            <div className="panel empty" style={{ boxShadow: 'none' }}><h3>No downloads</h3><p>Add your first download.</p></div>
          ) : (
            <table className="tx-table">
              <thead><tr><th>Name</th><th>Version</th><th>Status</th></tr></thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.downloadId} className={selected === d.downloadId && !creating ? 'row-selected' : 'row-click'}
                    onClick={() => { setSelected(d.downloadId); setCreating(false); }}>
                    <td>{d.name}<div className="acct-code">{CATEGORY_LABEL[d.category]}{d.platform ? ` · ${d.platform}` : ''}</div></td>
                    <td className="mono">{d.version}</td>
                    <td><span className={`status-dot ${d.active ? 'ok' : 'muted'}`}>{d.active ? 'Live' : 'Hidden'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          {editing ? (
            <DownloadEditor
              key={creating ? '__new__' : selected ?? ''}
              initial={editing}
              creating={creating}
              allDownloads={rows}
              onSaved={(d) => {
                qc.invalidateQueries({ queryKey: ['admin-downloads'] });
                qc.invalidateQueries({ queryKey: ['downloads'] });
                setCreating(false);
                setSelected(d.downloadId);
              }}
              onDeleted={() => {
                qc.invalidateQueries({ queryKey: ['admin-downloads'] });
                qc.invalidateQueries({ queryKey: ['downloads'] });
                setSelected(null);
                setCreating(false);
              }}
              onCatalogChanged={() => {
                qc.invalidateQueries({ queryKey: ['admin-downloads'] });
                qc.invalidateQueries({ queryKey: ['downloads'] });
              }}
            />
          ) : (
            <div className="panel empty"><h3>Select a download</h3><p>Choose a download to edit, or add a new one.</p></div>
          )}
        </div>
      </div>
    </div>
  );
}

/** A rewrite the manage dialog has already applied server-side. */
type GroupChange = { kind: 'rename'; from: string; to: string } | { kind: 'delete'; name: string };

/**
 * PATCH /admin/downloads/:id replaces the whole item rather than merging, so every field has to be
 * resent — most importantly `active`, which the API defaults to `true` when omitted (a hidden
 * download would quietly go live).
 */
function fullPayload(d: Download, deviceGroups: string[]) {
  return {
    name: d.name,
    category: d.category,
    version: d.version,
    fileKey: d.fileKey,
    fileName: d.fileName,
    deviceGroups,
    dependencies: d.dependencies ?? [],
    active: d.active,
    ...(d.description ? { description: d.description } : {}),
    ...(d.platform ? { platform: d.platform } : {}),
    ...(d.sizeBytes !== undefined ? { sizeBytes: d.sizeBytes } : {}),
    ...(d.contentType ? { contentType: d.contentType } : {}),
  };
}

/** Normalize a typed group name. Commas were the old delimiter — strip them so one entry can't
 *  masquerade as two. */
const cleanGroupName = (s: string) => s.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Group selection for a single download. Text entry is deliberately confined to the "New group"
 * field: the old free-text comma list couldn't be typed into at all, because the change handler
 * split on comma and dropped empty segments, erasing the separator on every keystroke.
 */
function GroupPicker({ value, catalog, onChange, onManage }: {
  value: string[];
  catalog: string[];
  onChange: (next: string[]) => void;
  onManage: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const available = catalog.filter((g) => !value.includes(g));

  function commit() {
    const cleaned = cleanGroupName(name);
    if (!cleaned) { setAdding(false); setName(''); return; }
    // Reuse the catalog's existing capitalization so "IB kojak" doesn't become a second group.
    const canonical = catalog.find((g) => g.toLowerCase() === cleaned.toLowerCase()) ?? cleaned;
    if (!value.includes(canonical)) onChange([...value, canonical]);
    setName('');
    setAdding(false);
  }

  return (
    <div className="dl-groups">
      {value.length > 0 ? (
        <div className="dl-group-row">
          {value.map((g) => (
            <span key={g} className="chip chip-on">
              {g}
              <button type="button" className="chip-x" title={`Remove ${g}`} aria-label={`Remove ${g}`}
                onClick={() => onChange(value.filter((x) => x !== g))}>✕</button>
            </span>
          ))}
        </div>
      ) : (
        <p className="field-help" style={{ margin: 0 }}>No groups yet — add one below.</p>
      )}

      {available.length > 0 && (
        <>
          <span className="field-help" style={{ marginTop: 10 }}>Available — click to add:</span>
          <div className="dl-group-row">
            {available.map((g) => (
              <button type="button" key={g} className="chip" onClick={() => onChange([...value, g])}>+ {g}</button>
            ))}
          </div>
        </>
      )}

      <div className="dl-group-row" style={{ marginTop: 10 }}>
        {adding ? (
          <>
            <input className="input" autoFocus value={name} style={{ maxWidth: 280 }}
              placeholder="e.g. Integrated Biometrics Kojak"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                else if (e.key === 'Escape') { setAdding(false); setName(''); }
              }} />
            <button type="button" className="btn btn-primary btn-sm" onClick={commit} disabled={!cleanGroupName(name)}>Add</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setName(''); }}>Cancel</button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>+ New group…</button>
            {catalog.length > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={onManage}>Manage groups</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Rename or delete a group across the whole catalog. Groups aren't a stored entity — they exist only
 * as strings on downloads — so both operations fan out a full-item PATCH to every download that
 * references the group. The fan-out isn't atomic: partial failures are reported so the operation can
 * be retried rather than silently leaving a half-renamed catalog.
 */
function ManageGroupsDialog({ allDownloads, onApplied, onRefresh, onClose }: {
  allDownloads: Download[];
  /** Called only when every affected download was rewritten, so the open editor never shows a
   *  change the server rejected. */
  onApplied: (change: GroupChange) => void;
  /** Called after any attempt, successful or not — a partial failure still changed server state. */
  onRefresh: () => void;
  onClose: () => void;
}) {
  const usage = new Map<string, number>();
  for (const d of allDownloads) for (const g of d.deviceGroups ?? []) usage.set(g, (usage.get(g) ?? 0) + 1);
  const groups = Array.from(usage.keys()).sort((a, b) => a.localeCompare(b));

  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  /** Rewrite `group` on every download that has it. Returns the number of failed PATCHes. */
  async function rewrite(group: string, map: (groups: string[]) => string[]): Promise<number> {
    const affected = allDownloads.filter((d) => (d.deviceGroups ?? []).includes(group));
    const results = await Promise.allSettled(
      affected.map((d) => api.patch<Download>(`/admin/downloads/${d.downloadId}`, fullPayload(d, map(d.deviceGroups ?? [])))),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed) setErr(`${affected.length - failed} of ${affected.length} downloads updated — ${failed} failed. Reopen and retry to finish.`);
    return failed;
  }

  async function doRename(from: string) {
    const to = cleanGroupName(draft);
    if (!to || to === from) { setRenaming(null); return; }
    setBusy(from); setErr(null);
    // Renaming onto an existing group merges the two; the Set dedupes downloads that had both.
    const failed = await rewrite(from, (gs) => Array.from(new Set(gs.map((g) => (g === from ? to : g)))));
    setBusy(null);
    onRefresh();
    // Leave the row open on failure so the error stays visible and the operation can be retried.
    if (failed) return;
    setRenaming(null);
    onApplied({ kind: 'rename', from, to });
  }

  async function doDelete(name: string) {
    setBusy(name); setErr(null);
    const failed = await rewrite(name, (gs) => gs.filter((g) => g !== name));
    setBusy(null);
    onRefresh();
    if (failed) return;
    setConfirming(null);
    onApplied({ kind: 'delete', name });
  }

  return (
    <div className="confirm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="confirm-card dl-manage-card" role="dialog" aria-modal="true" aria-label="Manage scanner groups">
        <h3 className="confirm-title">Manage scanner groups</h3>
        <p className="field-help" style={{ marginTop: 0 }}>
          Renaming or deleting a group updates every download that uses it. To create a group, add it
          to a download with “New group”.
        </p>

        <div className="dl-manage-list">
          {groups.map((g) => {
            const n = usage.get(g) ?? 0;
            const working = busy === g;
            return (
              <div className="dl-manage-row" key={g}>
                {renaming === g ? (
                  <>
                    <input className="input" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); void doRename(g); }
                        else if (e.key === 'Escape') setRenaming(null);
                      }} />
                    <button className="btn btn-primary btn-sm" disabled={working || !cleanGroupName(draft)} onClick={() => void doRename(g)}>
                      {working ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn btn-ghost btn-sm" disabled={working} onClick={() => setRenaming(null)}>Cancel</button>
                  </>
                ) : confirming === g ? (
                  <>
                    <span className="dl-manage-name dl-manage-confirm">Delete “{g}”? It will be removed from {n} download{n === 1 ? '' : 's'}.</span>
                    <button className="btn btn-primary btn-sm" disabled={working} onClick={() => void doDelete(g)}>
                      {working ? 'Deleting…' : 'Delete'}
                    </button>
                    <button className="btn btn-ghost btn-sm" disabled={working} onClick={() => setConfirming(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span className="dl-manage-name">{g}</span>
                    <span className="dl-manage-count">used by {n}</span>
                    <button className="btn btn-ghost btn-sm" title="Rename"
                      onClick={() => { setDraft(g); setConfirming(null); setRenaming(g); }}>Rename</button>
                    <button className="btn btn-ghost btn-sm" title="Delete"
                      onClick={() => { setRenaming(null); setConfirming(g); }}>Delete</button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {err && <p className="cart-error" style={{ marginTop: 12 }}>{err}</p>}

        <div className="confirm-actions">
          <button className="btn btn-ghost" disabled={Boolean(busy)} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function DownloadEditor({
  initial, creating, allDownloads, onSaved, onDeleted, onCatalogChanged,
}: {
  initial: Draft;
  creating: boolean;
  allDownloads: Download[];
  onSaved: (d: Download) => void;
  onDeleted: () => void;
  /** Group rename/delete rewrites other downloads — refetch without changing the selection. */
  onCatalogChanged: () => void;
}) {
  const [form, setForm] = useState<Draft>(initial);
  const [err, setErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [managing, setManaging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setForm(initial); setErr(null); }, [initial]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Existing group names across the catalog — offered as suggestions to keep names consistent.
  const groupSuggestions = Array.from(new Set(allDownloads.flatMap((d) => d.deviceGroups ?? []))).sort();
  const deps = form.dependencies ?? [];
  const setDeps = (list: Dependency[]) => set('dependencies', list);
  const updateDep = (i: number, patch: Partial<Dependency>) => setDeps(deps.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addDep = () => setDeps([...deps, { name: '' }]);
  const removeDep = (i: number) => setDeps(deps.filter((_, idx) => idx !== i));

  async function onFile(file: File) {
    setUploading(true); setErr(null);
    try {
      const ct = file.type || 'application/octet-stream';
      const { url, key } = await api.post<{ url: string; key: string }>(
        '/admin/downloads/presign', { contentType: ct, fileName: file.name });
      const put = await fetch(url, { method: 'PUT', headers: { 'content-type': ct }, body: file });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      setForm((f) => ({ ...f, fileKey: key, fileName: file.name, sizeBytes: file.size, contentType: ct }));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        version: form.version.trim(),
        fileKey: form.fileKey,
        fileName: form.fileName,
        description: form.description?.trim() || undefined,
        platform: form.platform,
        deviceGroups: (form.deviceGroups ?? []).map((g) => g.trim()).filter(Boolean),
        dependencies: deps
          .map((d) => ({ name: d.name.trim(), minVersion: d.minVersion?.trim() || undefined, downloadId: d.downloadId || undefined, note: d.note?.trim() || undefined }))
          .filter((d) => d.name),
        sizeBytes: form.sizeBytes,
        contentType: form.contentType,
        active: form.active,
      };
      return creating
        ? api.post<Download>('/admin/downloads', payload)
        : api.patch<Download>(`/admin/downloads/${form.downloadId}`, payload);
    },
    onSuccess: (d) => { setErr(null); onSaved(d); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Save failed'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/admin/downloads/${form.downloadId}`),
    onSuccess: () => onDeleted(),
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Delete failed'),
  });

  const canSave = form.name.trim() && form.version.trim() && form.fileKey && !uploading;

  return (
    <div className="panel">
      <div className="panel-head"><h3 style={{ margin: 0 }}>{creating ? 'New download' : 'Edit download'}</h3></div>

      <div className="demo-grid" style={{ marginTop: 16 }}>
        <label className="field" style={{ gridColumn: '1 / -1' }}><span className="field-label">Name</span>
          <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. EFTCreator Device Service" /></label>

        <label className="field" style={{ gridColumn: '1 / -1' }}><span className="field-label">Description</span>
          <textarea className="input" rows={2} value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} /></label>

        <label className="field"><span className="field-label">Category</span>
          <select className="select" value={form.category} onChange={(e) => set('category', e.target.value as Draft['category'])}>
            <option value="device-service">Device Service (64-bit)</option>
            <option value="device-service-32">Device Service (32-bit)</option>
            <option value="driver">Driver</option>
            <option value="software">Software</option>
          </select></label>

        <label className="field"><span className="field-label">Version</span>
          <input className="input" value={form.version} onChange={(e) => set('version', e.target.value)} placeholder="e.g. 2.1.0" /></label>

        <label className="field"><span className="field-label">Platform</span>
          <select className="select" value={form.platform ?? 'any'} onChange={(e) => set('platform', e.target.value as Draft['platform'])}>
            <option value="windows">Windows</option>
            <option value="mac">macOS</option>
            <option value="linux">Linux</option>
            <option value="any">All platforms</option>
          </select></label>

        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="field-label">Scanner device groups</span>
          <span className="field-help" style={{ marginBottom: 8 }}>
            Files sharing a group are shown together when a customer selects that scanner.
          </span>
          <GroupPicker
            value={form.deviceGroups ?? []}
            catalog={groupSuggestions}
            onChange={(next) => set('deviceGroups', next)}
            onManage={() => setManaging(true)}
          />
        </div>

        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="field-label">Dependencies</span>
          <span className="field-help" style={{ marginBottom: 8 }}>
            Prerequisites to install/update with this download — e.g. a driver-library version a new Device Service requires. Optionally link to the file that satisfies it.
          </span>
          {deps.length > 0 && (
            <div className="po-items">
              <div className="po-row dep-row po-row-head"><span>Requirement</span><span>Min version</span><span>Links to file</span><span></span></div>
              {deps.map((d, i) => (
                <div className="po-row dep-row" key={i}>
                  <input className="input" value={d.name} onChange={(e) => updateDep(i, { name: e.target.value })} placeholder="e.g. IB Driver Library" />
                  <input className="input mono" value={d.minVersion ?? ''} onChange={(e) => updateDep(i, { minVersion: e.target.value })} placeholder="1.5.0" />
                  <select className="select" value={d.downloadId ?? ''} onChange={(e) => updateDep(i, { downloadId: e.target.value })}>
                    <option value="">— none —</option>
                    {allDownloads.filter((x) => x.downloadId !== form.downloadId).map((x) => (
                      <option key={x.downloadId} value={x.downloadId}>{x.name} (v{x.version})</option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeDep(i)} title="Remove">✕</button>
                </div>
              ))}
            </div>
          )}
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8, alignSelf: 'flex-start' }} onClick={addDep}>+ Add dependency</button>
        </div>

        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="field-label">File</span>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input ref={fileRef} type="file" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : form.fileKey ? 'Replace file' : 'Upload file'}
            </button>
            {form.fileName && <span className="mono" style={{ fontSize: 13 }}>{form.fileName} {form.sizeBytes ? `(${fmtSize(form.sizeBytes)})` : ''}</span>}
          </div>
          <span className="field-help" style={{ marginTop: 6 }}>Installer / driver binary. Uploaded directly to secure storage.</span>
        </div>

        <label className="checkbox" style={{ gridColumn: '1 / -1' }}>
          <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
          <span>Live (shown to customers)</span>
        </label>
      </div>

      {err && <p className="cart-error" style={{ marginTop: 14 }}>{err}</p>}

      {managing && (
        <ManageGroupsDialog
          allDownloads={allDownloads}
          onClose={() => setManaging(false)}
          onRefresh={onCatalogChanged}
          onApplied={(change) => {
            // Keep the open editor in step with the rewrite that just landed on the server.
            setForm((f) => {
              const cur = f.deviceGroups ?? [];
              return {
                ...f,
                deviceGroups: change.kind === 'delete'
                  ? cur.filter((g) => g !== change.name)
                  : Array.from(new Set(cur.map((g) => (g === change.from ? change.to : g)))),
              };
            });
          }}
        />
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
          {save.isPending ? 'Saving…' : creating ? 'Publish download' : 'Save changes'}
        </button>
        {!creating && (
          <button className="btn btn-ghost"
            onClick={() => { if (window.confirm(`Delete "${form.name}"? This removes the file too.`)) remove.mutate(); }}
            disabled={remove.isPending}>
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}
